import { CounterField, CounterSettingsDto } from '@/shared/openapi';

/**
 * Coerce stored counter DF data to a plain string for display/input.
 * Accepts string, number, or API `{ counter, value }`.
 */
export function unwrapCounterFieldValue(raw: unknown): string | null {
  if (raw === undefined || raw === null) {
    return null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (o['value'] != null && String(o['value']).trim() !== '') {
      return String(o['value']).trim();
    }
    if (o['counter'] != null && Number.isFinite(Number(o['counter']))) {
      return String(o['counter']);
    }
  }
  return null;
}

export function isCounterFieldObject(raw: unknown): raw is CounterField {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const o = raw as Record<string, unknown>;
  return (
    o['counter'] != null &&
    Number.isFinite(Number(o['counter'])) &&
    o['value'] != null &&
    String(o['value']).trim() !== ''
  );
}

/** Extract numeric counter from a formatted value using prefix/suffix settings. */
export function parseCounterNumberFromFormattedValue(
  formatted: string,
  settings?: CounterSettingsDto | null
): number | null {
  const trimmed = formatted.trim();
  if (!trimmed) {
    return null;
  }

  const prefix = settings?.prefix != null ? String(settings.prefix) : '';
  const suffix = settings?.suffix != null ? String(settings.suffix) : '';

  let middle = trimmed;
  if (prefix && middle.startsWith(prefix)) {
    middle = middle.slice(prefix.length);
  }
  if (suffix && middle.endsWith(suffix)) {
    middle = middle.slice(0, -suffix.length);
  }

  const digitsOnly = middle.replace(/\D/g, '');
  if (digitsOnly) {
    const n = parseInt(digitsOnly, 10);
    return Number.isFinite(n) ? n : null;
  }

  const match = trimmed.match(/(\d+)(?!.*\d)/);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

/**
 * Serialize a counter DF for API save (`{ counter: number, value: string }`).
 */
export function wrapCounterFieldForSave(
  raw: unknown,
  settings?: CounterSettingsDto | null,
  suggestion?: CounterField | null
): CounterField | null {
  if (isCounterFieldObject(raw)) {
    return {
      counter: Math.floor(Number(raw.counter)),
      value: String(raw.value).trim(),
    };
  }

  const valueStr = unwrapCounterFieldValue(raw);
  if (!valueStr) {
    return null;
  }

  if (suggestion?.value === valueStr) {
    return { counter: suggestion.counter, value: suggestion.value };
  }

  const counter = parseCounterNumberFromFormattedValue(valueStr, settings);
  if (counter != null) {
    return { counter, value: valueStr };
  }

  return null;
}
